/**
 * @file SelectDropdownView — presentational selection-dropdown surface.
 * @description The selection sibling of `DesktopDropdownMenu` (SE-5570):
 * renders option rows (label + optional bold value + right-aligned Check on
 * the selected row) inside a `FixedPositionDropdownShell` panel. The shell
 * supplies measured trigger-anchored positioning, viewport clamping with
 * automatic flip-above, backdrop/hardware-back dismissal, and the native
 * Modal host (SE-5568). Row markup generalizes the SE-5551/SE-5568
 * `renderShellOption` / `renderMetricShellOption` pattern from
 * `features/player-detail` so every selection dropdown shares one look.
 * @remarks This is a View component - contains no business logic, hooks, or state
 * @module components/molecules/SelectDropdown/SelectDropdownView
 */
import { Check, Search } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { Input } from "@/components/atoms/Input";
import {
  FixedPositionDropdownShell,
  FixedPositionDropdownShellBackdrop,
  FixedPositionDropdownShellPanel,
  FixedPositionDropdownShellTrigger,
  type FixedPositionDropdownShellTriggerRef,
  type DropdownMeasurableNode,
  type DropdownPanelGeometry,
} from "@/components/atoms/FixedPositionDropdownShell";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * How a row announces that it is selected.
 *
 * `check` is the single-select look: the chosen row recolors to the accent and
 * gains a trailing check. `checkbox` is the MULTI-select look — a leading box
 * that is either empty or filled, no accent recolor, and a raised row fill.
 * The distinction is semantic, not decorative: an accent-recoloured row reads
 * as "this is the value", which is the wrong promise on a menu where several
 * rows can be on at once. Pair `checkbox` with `keepOpenOnSelect`.
 */
export type SelectDropdownSelectionIndicator = "check" | "checkbox";

/** A single selectable option row. */
export interface SelectDropdownOption {
  /** Stable identity (React key + selection compare). */
  id: string;
  /** Visible option label. */
  label: string;
  /** Optional bold value rendered beside the label (metric-style rows). */
  value?: string | number | null;
  /**
   * Optional second line under the label, for a row whose consequence needs
   * stating rather than implying (SE-6506's save-as-default warning).
   *
   * Wraps rather than truncating, because a sentence cut at one line states
   * half a consequence. Omitted on every other row, which keeps their single
   * truncating line exactly as it was.
   */
  description?: string;
  /** Whether this option is the current selection (accent + Check). */
  selected: boolean;
  /** Selection handler (the container decorates close-on-select). */
  onPress: () => void;
  /** Optional row testID. */
  testID?: string;
  /**
   * Open the PINNED trailing group at this row.
   *
   * For menus that carry two different KINDS of choice rather than one list,
   * e.g. the board's sort menu: the sortable fields, then the direction
   * (SE-6554, mirroring the shared search-modal sort dropdown). Without the
   * rule the two groups read as one list of mutually exclusive options, which
   * they are not — one row from each group is active at once.
   *
   * This row and everything after it render OUTSIDE the scroll area, below a
   * divider, so the trailing group stays put while a long field list scrolls —
   * matching `SortDropdownView`, which keeps its hairline and direction rows
   * outside the ScrollView for the same reason.
   */
  startsGroup?: boolean;
  /**
   * Render the row inert and dimmed.
   *
   * The sort menu's direction rows use this rather than disappearing while no
   * field is sorted: a control that vanishes teaches nothing, while a dimmed
   * one advertises that direction exists and is waiting on a field.
   */
  disabled?: boolean;
}

/** Public props for the SelectDropdown primitive. */
export interface SelectDropdownProps {
  /** Whether the dropdown is open. */
  isOpen: boolean;
  /** Invoked when the dropdown requests close (outside tap, back, select). */
  onClose: () => void;
  /** Toggles the dropdown — wired to the trigger press. */
  onToggle: () => void;
  /** Trigger content — the anchor visual (pill, chip, …). */
  triggerContent: React.ReactNode;
  /** testID applied to the measurable trigger pressable. */
  triggerTestID?: string;
  /** Accessibility label for the trigger pressable. */
  triggerAccessibilityLabel?: string;
  /** Closed-vocabulary classes for the trigger pressable. */
  triggerClassName?: string;
  /**
   * Closed-vocabulary classes for the shell's relative wrapper — the element
   * that is the flex item wherever the dropdown is placed.
   *
   * The wrapper is a react-native-web View, so it does not shrink by default.
   * A caller that needs the dropdown to give way inside a crowded row has to
   * say so here; `triggerClassName` is one level too deep to reach it.
   */
  rootClassName?: string;
  /** Option rows to render. */
  options: ReadonlyArray<SelectDropdownOption>;
  /** Which trigger edge the panel hugs. Defaults to "left". */
  anchor?: "left" | "right";
  /** Fixed panel width in px (optional). */
  panelWidth?: number;
  /** Minimum panel width in px. Defaults to 192 (SE-5149 floor). */
  panelMinWidth?: number;
  /** Panel max height in px (scroll + clamp/flip). Defaults to 280. */
  panelHeight?: number;
  /** Keep the panel open after selecting (multi-interact dropdowns). */
  keepOpenOnSelect?: boolean;
  /** Extra closed-vocabulary classes for the panel. */
  panelClassName?: string;
  /** Extra closed-vocabulary classes for every option row. */
  rowClassName?: string;
  /** Optional heading rendered above the first row (e.g. "Filter by watchlist"). */
  header?: string;
  /**
   * Current text in the panel's search field.
   *
   * Search is OPT-IN and exists for menus whose option list is long enough that
   * scrolling is not a way to find anything — the board's share picker lists
   * every active member of the organization (500 per page since SE-5378), where
   * a plain scroll had people concluding a teammate was missing. Filtering is
   * the CALLER's job: pass `options` already filtered by `searchValue`, exactly
   * as it would filter any other derived list. The dropdown only renders the
   * field and reports keystrokes.
   *
   * Providing `onSearchChange` is what mounts the field; without it the panel
   * keeps its pre-SE-6554 shape and no existing caller changes.
   */
  searchValue?: string;
  /** Keystroke handler for the panel's search field. Mounts the field. */
  onSearchChange?: (text: string) => void;
  /** Placeholder + accessible name for the search field. Defaults to "Search". */
  searchPlaceholder?: string;
  /**
   * Message shown in place of the rows when there are none.
   *
   * Without it an over-narrow search renders an empty box that reads as a
   * broken panel rather than as "nothing matched".
   */
  emptyText?: string;
  /** How rows announce selection. Defaults to `"check"` (single-select). */
  selectionIndicator?: SelectDropdownSelectionIndicator;
  /** Root testID. */
  testID?: string;
}

/** Props for the presentational view (defaults + positioning resolved). */
export interface SelectDropdownViewProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  triggerContent: React.ReactNode;
  triggerTestID?: string;
  triggerAccessibilityLabel?: string;
  triggerClassName?: string;
  rootClassName?: string;
  /** Rows inside the scroll area (before any pinned group). */
  scrollingOptions: ReadonlyArray<SelectDropdownOption>;
  /** Rows pinned below the divider, outside the scroll area. */
  pinnedOptions: ReadonlyArray<SelectDropdownOption>;
  panelClassName?: string;
  rowClassName?: string;
  header?: string;
  searchValue?: string;
  onSearchChange?: (text: string) => void;
  searchPlaceholder?: string;
  emptyText?: string;
  selectionIndicator?: SelectDropdownSelectionIndicator;
  testID?: string;
  /** Measurement ref from `useDropdownPositioning` (container-owned). */
  triggerRef: React.RefObject<DropdownMeasurableNode | null>;
  /** Style-ready panel geometry (null until measured). */
  panelGeometry: DropdownPanelGeometry | null;
  /** SE-4953 flicker guard — overlay renders only when true. */
  shouldShow: boolean;
}

/**
 * Renders one option row — label, optional bold value, Check when selected.
 * Plain render helper (not a component) per the SE-5551 precedent.
 * @param option - The option to render (onPress already decorated).
 * @param rowClassName - Extra classes appended to the row.
 * @returns The option Pressable.
 */
const renderOption = (
  option: SelectDropdownOption,
  rowClassName?: string
): React.ReactElement => (
  <Pressable
    key={option.id}
    accessibilityRole="menuitem"
    accessibilityLabel={option.label}
    accessibilityState={{
      selected: option.selected,
      disabled: option.disabled === true,
    }}
    disabled={option.disabled === true}
    onPress={option.onPress}
    testID={option.testID}
    className={`w-full flex-row items-center justify-between gap-2 px-3 py-2 ${
      option.disabled === true ? "opacity-40" : ""
    } ${rowClassName ?? ""}`.trim()}
  >
    <Stack className="min-w-0 shrink gap-px">
      <Stack
        direction="horizontal"
        className="min-w-0 shrink items-center gap-2"
      >
        <Text
          variant={option.selected ? "body-strong" : "body"}
          className={`shrink ${
            option.selected ? "text-accent-primary" : "text-content-primary"
          }`}
          numberOfLines={1}
          isTruncated
        >
          {option.label}
        </Text>
        {option.value != null ? (
          <Text
            variant="body-bold"
            className={
              option.selected ? "text-accent-primary" : "text-content-secondary"
            }
          >
            {option.value}
          </Text>
        ) : null}
      </Stack>
      {option.description != null && option.description !== "" ? (
        <Text variant="caption" className="text-content-muted">
          {option.description}
        </Text>
      ) : null}
    </Stack>
    {option.selected && option.disabled !== true ? (
      <Icon as={Check} size="sm" className="text-accent-primary" />
    ) : null}
  </Pressable>
);

/**
 * Renders one MULTI-select row — leading 16px box, then the label.
 *
 * The row keeps one label colour in both states and carries the selection in
 * the box and the row fill, so a menu with several rows on does not read as a
 * menu with several competing "current values".
 * @param option - The option to render (onPress already decorated).
 * @param rowClassName - Extra classes appended to the row.
 * @returns The option Pressable.
 */
const renderCheckboxOption = (
  option: SelectDropdownOption,
  rowClassName?: string
): React.ReactElement => (
  <Pressable
    key={option.id}
    accessibilityRole="checkbox"
    accessibilityLabel={option.label}
    accessibilityState={{ checked: option.selected }}
    onPress={option.onPress}
    testID={option.testID}
    className={`w-full flex-row items-center gap-2 rounded-sm px-3 py-3 ${
      option.selected ? "bg-surface-raised" : ""
    } ${rowClassName ?? ""}`.trim()}
  >
    <Box
      className={`h-4 w-4 shrink-0 items-center justify-center rounded-sm border-2 ${
        option.selected
          ? "border-surface-inverse bg-surface-inverse"
          : "border-outline-strong"
      }`}
    >
      {option.selected ? (
        <Icon as={Check} size="2xs" className="text-content-inverse" />
      ) : null}
    </Box>
    <Text
      variant="body"
      className="shrink text-content-secondary"
      numberOfLines={1}
      isTruncated
    >
      {option.label}
    </Text>
  </Pressable>
);

/**
 * Renders one option row with the requested selection indicator.
 * @param option - The option to render (onPress already decorated).
 * @param selectionIndicator - How rows announce selection.
 * @param rowClassName - Extra classes appended to the row.
 * @returns The option row.
 */
const renderRow = (
  option: SelectDropdownOption,
  selectionIndicator: SelectDropdownSelectionIndicator,
  rowClassName?: string
): React.ReactElement =>
  selectionIndicator === "checkbox"
    ? renderCheckboxOption(option, rowClassName)
    : renderOption(option, rowClassName);

/**
 * Renders the panel's optional search field.
 *
 * `onSearchChange` is the mount switch, not `searchValue`: a controlled field
 * sitting at "" is indistinguishable from an absent one by its value alone.
 * @param onSearchChange - Keystroke handler (absent = no field).
 * @param searchValue - Current text.
 * @param searchPlaceholder - Placeholder + accessible name.
 * @returns The search field, or null when the caller wants none.
 */
const renderSearch = (
  onSearchChange: ((text: string) => void) | undefined,
  searchValue: string | undefined,
  searchPlaceholder: string | undefined
): React.ReactElement | null => {
  if (onSearchChange === undefined) return null;
  const label = searchPlaceholder ?? "Search";
  return (
    <Box className="px-2 pb-1 pt-2">
      <Input
        variant="outline"
        size="sm"
        className="h-9 rounded-sm border-outline-strong data-[focus=true]:border-outline-strong"
        leadingIcon={Search}
        leadingIconTone="muted"
        field={{
          value: searchValue ?? "",
          onChangeText: onSearchChange,
          placeholder: label,
          "aria-label": label,
        }}
      />
    </Box>
  );
};

/**
 * Splits options into the scrolling list and the pinned trailing group.
 *
 * The first row flagged `startsGroup` and everything after it belong to the
 * pinned group; when no row carries the flag every option scrolls, which is the
 * shape every pre-SE-6554 caller has.
 * @param options - All option rows in display order.
 * @returns The scrolling rows and the pinned rows.
 */
export const splitPinnedGroup = (
  options: ReadonlyArray<SelectDropdownOption>
): {
  scrolling: ReadonlyArray<SelectDropdownOption>;
  pinned: ReadonlyArray<SelectDropdownOption>;
} => {
  const groupStart = options.findIndex(option => option.startsGroup === true);
  if (groupStart === -1) return { scrolling: options, pinned: [] };
  return {
    scrolling: options.slice(0, groupStart),
    pinned: options.slice(groupStart),
  };
};

/**
 * SelectDropdownView — stateless selection surface on the shell.
 * @param props - View props.
 * @param props.isOpen - Whether the dropdown is open.
 * @param props.onClose - Invoked when the dropdown requests close.
 * @param props.onToggle - Toggles the dropdown (trigger press).
 * @param props.triggerContent - Anchor visual content.
 * @param props.triggerTestID - testID for the trigger pressable.
 * @param props.triggerAccessibilityLabel - A11y label for the trigger.
 * @param props.triggerClassName - Classes for the trigger pressable.
 * @param props.rootClassName - Classes for the shell's relative wrapper.
 * @param props.scrollingOptions - Rows inside the scroll area.
 * @param props.pinnedOptions - Rows pinned below the divider.
 * @param props.panelClassName - Extra classes for the panel.
 * @param props.rowClassName - Extra classes for every row.
 * @param props.header - Optional heading above the first row.
 * @param props.searchValue - Current text in the panel's search field.
 * @param props.onSearchChange - Keystroke handler; providing it mounts the field.
 * @param props.searchPlaceholder - Placeholder + accessible name for the field.
 * @param props.emptyText - Message shown in place of the rows when there are none.
 * @param props.selectionIndicator - How rows announce selection.
 * @param props.testID - Root testID.
 * @param props.triggerRef - Measurement ref for the shell trigger.
 * @param props.panelGeometry - Measured panel geometry.
 * @param props.shouldShow - Overlay render guard.
 * @returns The anchored selection dropdown.
 */
const SelectDropdownView = ({
  isOpen,
  onClose,
  onToggle,
  triggerContent,
  triggerTestID,
  triggerAccessibilityLabel,
  triggerClassName,
  rootClassName,
  scrollingOptions,
  pinnedOptions,
  panelClassName,
  rowClassName,
  header,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  emptyText,
  selectionIndicator = "check",
  testID,
  triggerRef,
  panelGeometry,
  shouldShow,
}: SelectDropdownViewProps): React.ReactElement => (
  <FixedPositionDropdownShell testID={testID} className={rootClassName}>
    <FixedPositionDropdownShellTrigger
      ref={triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>}
      isOpen={isOpen}
      onPress={onToggle}
      testID={triggerTestID}
      accessibilityLabel={triggerAccessibilityLabel}
      className={triggerClassName}
    >
      {triggerContent}
    </FixedPositionDropdownShellTrigger>
    {shouldShow ? (
      <>
        <FixedPositionDropdownShellBackdrop
          onPress={onClose}
          accessibilityLabel="Close dropdown"
        />
        <FixedPositionDropdownShellPanel
          geometry={panelGeometry}
          onDismiss={onClose}
          className={`overflow-hidden py-1 ${panelClassName ?? ""}`.trim()}
        >
          {header === undefined ? null : (
            <Text variant="caption" className="px-3 py-2 text-content-primary">
              {header}
            </Text>
          )}
          {renderSearch(onSearchChange, searchValue, searchPlaceholder)}
          {scrollingOptions.length === 0 &&
          pinnedOptions.length === 0 &&
          emptyText !== undefined ? (
            <Text
              variant="caption"
              className="px-3 py-4 text-center text-content-muted"
            >
              {emptyText}
            </Text>
          ) : null}
          <ScrollView>
            {scrollingOptions.map(option =>
              renderRow(option, selectionIndicator, rowClassName)
            )}
          </ScrollView>
          {pinnedOptions.length === 0 ? null : (
            <>
              <Box className="py-1">
                <Divider className="bg-outline-subtle" />
              </Box>
              {pinnedOptions.map(option =>
                renderRow(option, selectionIndicator, rowClassName)
              )}
            </>
          )}
        </FixedPositionDropdownShellPanel>
      </>
    ) : null}
  </FixedPositionDropdownShell>
);

SelectDropdownView.displayName = "SelectDropdownView";

export default memo(SelectDropdownView);
